import type { Module } from "@/interfaces";

export interface MenuEntry {
  route?: string;
  hasSubMenu: boolean;
  isActive: boolean;
  children: Module[];
}

export function normalizeRoute(route?: string | null): string | null {
  if (typeof route !== "string") return null;
  const trimmed = route.trim().replace(/^\/+|\/+$/g, "");
  return trimmed === "" ? null : `/${trimmed}`;
}

// Casa a rota mais longa: evita que /dashboard acenda estando em /dashboard/trunk,
// mas mantem o modulo aceso em paginas de detalhe como /vehicles/123.
export function findActiveRoute(
  modules: Module[],
  pathname: string,
): string | null {
  const current = normalizeRoute(pathname) ?? "/";
  let best: string | null = null;

  for (const module of modules) {
    const route = normalizeRoute(module.route);
    if (!route) continue;
    if (current !== route && !current.startsWith(`${route}/`)) continue;
    if (best === null || route.length > best.length) best = route;
  }

  return best;
}

export function buildModuleTree(modules: Module[]): Module[] {
  const moduleMap = new Map<number, Module>();
  const roots: Module[] = [];

  modules.forEach((module) =>
    moduleMap.set(module.id, { ...module, children: [] }),
  );

  modules.forEach((module) => {
    const node = moduleMap.get(module.id);
    if (!node) return;

    if (module.parent_id) {
      moduleMap.get(module.parent_id)?.children?.push(node);
    } else {
      roots.push(node);
    }
  });

  const byOrder = (a: Module, b: Module) => a.order - b.order;
  moduleMap.forEach((node) => node.children?.sort(byOrder));

  return roots.sort(byOrder);
}

// Um pai sem rota propria e com um unico filho navegavel nao oferece escolha
// nenhuma: vira item de um clique so, com o rotulo do pai e a rota do filho.
export function resolveMenuEntry(
  module: Module,
  activeRoute: string | null,
): MenuEntry {
  const children = (module.children ?? []).filter(
    (child) => !!normalizeRoute(child.route),
  );

  const isFlattened = !normalizeRoute(module.route) && children.length === 1;
  const hasSubMenu = !isFlattened && children.length > 0;
  const route = isFlattened ? children[0].route : module.route;
  const normalized = normalizeRoute(route);

  const isActive = hasSubMenu
    ? children.some((child) => normalizeRoute(child.route) === activeRoute)
    : normalized !== null && normalized === activeRoute;

  return { route, hasSubMenu, isActive, children };
}
